import { ColDef, GridOptions, HeaderValueGetterParams, Theme } from 'ag-grid-enterprise';
import { AgGridReact, AgGridReactProps, CustomTooltipProps } from 'ag-grid-react';
import { useMemo } from 'react';

import './setup'; // Important: module and license key registration
import { borderedTheme, defaultTheme, getTableTheme, gridTheme } from './theme';

import { TooltipContent } from '~/components/scales/Tooltip/TooltipOverlay';
import { StrictUnion } from '~/utilities/types';
import './theme-overrides.css';

// Selected list of grid option props we want to expose.
// These are extensively documented here: https://www.ag-grid.com/react-data-grid/grid-options/
type Props<TData> = Pick<
  AgGridReactProps<TData>,
  | 'aggFuncs'
  | 'autoGroupColumnDef'
  | 'autoSizeStrategy'
  | 'cellSelection'
  | 'columnDefs'
  | 'columnTypes'
  | 'domLayout'
  | 'enableCellTextSelection'
  | 'enableFilterHandlers'
  | 'excelStyles'
  | 'getContextMenuItems'
  | 'getMainMenuItems'
  | 'getRowStyle'
  | 'grandTotalRow'
  | 'groupAllowUnbalanced'
  | 'groupDefaultExpanded'
  | 'groupLockGroupColumns'
  | 'initialState'
  | 'isGroupOpenByDefault'
  | 'loading'
  | 'maintainColumnOrder'
  | 'noRowsOverlayComponent'
  | 'onCellValueChanged'
  | 'onColumnVisible'
  | 'onFilterChanged'
  | 'onFirstDataRendered'
  | 'onGridReady'
  | 'onRowDragEnd'
  | 'onRowDragEnter'
  | 'onRowDragMove'
  | 'onSelectionChanged'
  | 'onStateUpdated'
  | 'overlayNoRowsTemplate'
  | 'rowBuffer'
  | 'rowClassRules'
  | 'rowData'
  | 'rowDragManaged'
  | 'rowGroupPanelShow'
  | 'rowHeight'
  | 'rowSelection'
  | 'sideBar'
  | 'suppressCellFocus'
  | 'suppressContextMenu'
  | 'suppressGroupChangesColumnVisibility'
> & {
  // We provide a base defaultColDef with flex, minWidth, and `editable: false` because editability isn't supported yet.
  // If you want to provide your own defaultColDef, you'll probably want to include these as well.
  defaultColDef?: AgGridReactProps<TData>['defaultColDef'];
  /**
   * We've made this required to force better UI across data changes.
   *   - https://www.ag-grid.com/react-data-grid/data-update-row-data/
   */
  getRowId: AgGridReactProps<TData>['getRowId'];
  /**
   * context allows you to provide a custom context object that's passed to all callbacks and events in the grid.
   * This is useful for providing global information like currency or locale settings to be used in `valueFormatter`s.
   * https://www.ag-grid.com/react-data-grid/context/
   */
  context?: AgGridReactProps<TData>['context'];
  gridId: string;
} & VariantProps;

type VariantProps = StrictUnion<
  | { type: 'grid' }
  | { type: 'table'; height?: 'dense' | 'default' }
  | {
      type?: undefined;
      /** @deprecated will be removed as part of CT-1929 */
      themeName?: ThemeName;
    }
>;

const DEFAULT_COL_DEF: ColDef = {
  minWidth: 72, // this is roughly the width of one character (e.g. "#") and the menu icon next to it
  editable: false, // Set to false by default, columns can override this
  lockPinned: true, // initially, we're not supporting manual column pinning
  tooltipComponent: (props: CustomTooltipProps) => <TooltipContent>{props.value}</TooltipContent>,
};

type ThemeName = 'default' | 'bordered';

/** @deprecated these themes are being phased out */
const deprecatedThemes: Record<ThemeName, Theme> = {
  default: defaultTheme,
  bordered: borderedTheme,
};

export function Grid<TData>(props: Omit<Props<TData>, 'type' | 'themeName' | 'height'>) {
  return <InternalGrid {...props} type="grid" />;
}

export function Table<TData>(
  props: Omit<Props<TData>, 'type' | 'themeName'> & { height?: 'dense' | 'default' }
) {
  return <InternalGrid {...props} height={props.height} type="table" />;
}

// TODO CT-1960: Deprecate this component once Grid/Table are exported out of
// scales/index.tsx. Until then this is publicly available "Grid" component.
export default function AGGrid<TData>(props: Omit<Props<TData>, 'type' | 'height'>) {
  return <InternalGrid {...props} />;
}

function InternalGrid<TData>(props: Props<TData>) {
  let theme: Theme | undefined;
  let gridOptions: GridOptions | undefined;

  if (props.type === 'grid') {
    theme = gridTheme;
    gridOptions = {
      singleClickEdit: false,
      stopEditingWhenCellsLoseFocus: true,
      cellSelection: { suppressMultiRanges: false },
    };
  } else if (props.type === 'table') {
    theme = getTableTheme(props.height ?? 'default');
    gridOptions = {
      singleClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,
    };
  } else {
    theme = deprecatedThemes[props.themeName ?? 'default'];
    gridOptions = {
      stopEditingWhenCellsLoseFocus: true,
    };
  }

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerValueGetter: (params) => buildNestedGroupLabel(params.api),
      headerTooltipValueGetter: (params) => buildNestedGroupLabel(params.api),
      tooltipComponent: (props: CustomTooltipProps) => (
        <TooltipContent>{props.value}</TooltipContent>
      ),
      ...props.autoGroupColumnDef,
    } satisfies ColDef<TData>;
  }, [props.autoGroupColumnDef]);

  return (
    <AgGridReact
      aggFuncs={props.aggFuncs}
      autoGroupColumnDef={autoGroupColumnDef}
      autoSizeStrategy={props.autoSizeStrategy}
      cellSelection={props.cellSelection}
      columnDefs={props.columnDefs}
      columnTypes={props.columnTypes}
      context={props.context}
      defaultColDef={props.defaultColDef ?? DEFAULT_COL_DEF}
      defaultExcelExportParams={{
        // Match row group expand/collapse state when exporting to Excel (default is all expanded)
        rowGroupExpandState: 'match',
      }}
      domLayout={props.domLayout}
      enableCellTextSelection={props.enableCellTextSelection}
      enableFilterHandlers={props.enableFilterHandlers}
      excelStyles={props.excelStyles}
      getContextMenuItems={props.getContextMenuItems}
      getLocaleText={getLocaleTextOverrides}
      getMainMenuItems={props.getMainMenuItems}
      getRowId={props.getRowId}
      getRowStyle={props.getRowStyle}
      grandTotalRow={props.grandTotalRow}
      gridId={props.gridId}
      gridOptions={gridOptions}
      groupAllowUnbalanced={props.groupAllowUnbalanced}
      groupDefaultExpanded={props.groupDefaultExpanded}
      groupLockGroupColumns={props.groupLockGroupColumns}
      initialState={props.initialState}
      isGroupOpenByDefault={props.isGroupOpenByDefault}
      loading={props.loading}
      maintainColumnOrder={props.maintainColumnOrder}
      noRowsOverlayComponent={props.noRowsOverlayComponent}
      onCellValueChanged={props.onCellValueChanged}
      onColumnVisible={props.onColumnVisible}
      onFilterChanged={props.onFilterChanged}
      onFirstDataRendered={props.onFirstDataRendered}
      onGridReady={props.onGridReady}
      onRowDragEnd={props.onRowDragEnd}
      onRowDragEnter={props.onRowDragEnter}
      onRowDragMove={props.onRowDragMove}
      onSelectionChanged={props.onSelectionChanged}
      onStateUpdated={props.onStateUpdated}
      overlayNoRowsTemplate={props.overlayNoRowsTemplate}
      rowBuffer={props.rowBuffer}
      rowClassRules={props.rowClassRules}
      rowData={props.rowData}
      rowDragManaged={props.rowDragManaged}
      rowGroupPanelShow={props.rowGroupPanelShow}
      rowHeight={props.rowHeight}
      rowSelection={props.rowSelection}
      selectionColumnDef={props.rowSelection ? { pinned: 'left' } : undefined}
      sideBar={props.sideBar}
      suppressAggFuncInHeader // just show "Total" instead of "sum(Total)" in the column header when grouping
      suppressCellFocus={props.suppressCellFocus}
      suppressContextMenu={props.suppressContextMenu}
      suppressGroupChangesColumnVisibility={props.suppressGroupChangesColumnVisibility}
      suppressScrollOnNewData
      theme={theme}
      tooltipShowDelay={100} // matches the Scale Tooltip delay
    />
  );
}

// Customizes locale text for specific keys in the grid. This allows us to
// override copy displayed in the grid.
function getLocaleTextOverrides(params: { key: string; defaultValue: string }) {
  // To see all visible keys and default values, uncomment the following:
  // console.log(params.key, params.defaultValue);
  switch (params.key) {
    case 'rowGroupColumnsEmptyMessage':
      // Message displayed when the row group panel is empty
      return 'Drag column headers here to group rows';
    default:
      // Return the default value for any keys not explicitly overridden
      return params.defaultValue;
  }
}

function buildNestedGroupLabel(api: HeaderValueGetterParams['api']): string {
  const groupCols = api.getRowGroupColumns();
  const label = groupCols
    .map((col) => api.getDisplayNameForColumn(col, 'header'))
    .filter((name) => name.trim() !== '')
    .join(' / ');
  return label || 'Group';
}
